/**
 * Fantasy Console 0 - Cart Context
 *
 * Provides global state management for the current cart being edited.
 * Supports multi-file carts and Supabase cloud storage.
 */

import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
  useMemo,
  type ReactNode,
} from 'react'
import {
  Cart,
  CodeFile,
  CartMetadata,
  createEmptyCart,
  encodeBase64,
} from '@fc0/types'
import type { SFX } from '@fc0/types'
import { encodeSFX } from '@fc0/console'
import {
  saveCurrentCart,
  loadCurrentCart,
  saveCloudSaveHash,
  loadCloudSaveHash,
} from '../utils/storage'
import {
  TabId,
  ViewId,
  EditorTabId,
  getViewForTab,
  isEditorTab,
} from '../types'
import { CartService } from '../services/cartService'
import { CartLibraryCache } from '../services/cartLibraryCache'
import { ThumbnailService } from '../services/thumbnailService'
import { isSupabaseConfigured } from '../lib/supabase'
import { useConsole } from './ConsoleContext'

interface CartContextType {
  cart: Cart
  updateCart: (cart: Cart) => void
  updateCodeFiles: (codeFiles: CodeFile[]) => void
  updateCodeFile: (index: number, content: string) => void
  updateSprites: (sprites: Uint8Array) => void
  updateMap: (map: Uint8Array) => void
  updateSfx: (sfxBank: SFX[]) => void
  updateMetadata: (metadata: Partial<CartMetadata>) => void
  createNewCart: (metadata?: Partial<CartMetadata>) => void
  loadCart: (cart: Cart, ownerId?: string) => void
  addCodeFile: (name: string) => boolean
  removeCodeFile: (index: number) => boolean
  renameCodeFile: (index: number, newName: string) => boolean
  saveToCloud: () => Promise<void>
  forkCurrentCart: () => Promise<string>
  isDirty: boolean
  markClean: () => void
  activeTab: TabId
  activeView: ViewId
  lastEditorTab: EditorTabId
  setActiveTab: (tab: TabId) => void
  saveStatus: 'saved' | 'saving' | 'unsaved'
  cloudSaveEnabled: boolean
  // Cloud change detection
  hasUnsavedCloudChanges: boolean
  lastCloudSaveTime: Date | null
  currentCartHash: string
  updateCloudSaveHash: () => void
  // Cart load tracking
  cartLoadVersion: number
}

const CartContext = createContext<CartContextType | null>(null)

const AUTOSAVE_DELAY = 1500
const MAX_CODE_FILES = 8

export function CartProvider({ children }: { children: ReactNode }) {
  const { captureCanvas } = useConsole()
  const [cart, setCart] = useState<Cart>(() => {
    const saved = loadCurrentCart()
    return saved || createEmptyCart()
  })

  const [isDirty, setIsDirty] = useState(false)
  const [saveStatus, setSaveStatus] = useState<'saved' | 'saving' | 'unsaved'>(
    'saved',
  )
  const [activeTab, setActiveTabRaw] = useState<TabId>('code')
  const [lastEditorTab, setLastEditorTab] = useState<EditorTabId>('code')

  const activeView = getViewForTab(activeTab)

  const setActiveTab = useCallback((tab: TabId) => {
    setActiveTabRaw(tab)
    if (isEditorTab(tab)) {
      setLastEditorTab(tab)
    }
  }, [])

  // Cart load tracking
  const [cartLoadVersion, setCartLoadVersion] = useState(0)

  // Cloud save tracking
  const [lastCloudSaveHash, setLastCloudSaveHash] = useState<string | null>(
    () => loadCloudSaveHash(),
  )
  const [lastCloudSaveTime, setLastCloudSaveTime] = useState<Date | null>(
    () => {
      if (cart.id && cart.updatedAt && loadCloudSaveHash()) {
        return new Date(cart.updatedAt)
      }
      return null
    },
  )

  const saveTimeoutRef = useRef<number | null>(null)
  const cartRef = useRef(cart)
  cartRef.current = cart
  const cloudSaveEnabled = isSupabaseConfigured()

  // Compute current cart hash for change detection
  const currentCartHash = useMemo(() => {
    return JSON.stringify({
      codeFiles: cart.codeFiles,
      sprites: cart.sprites,
      map: cart.map,
      sfx: cart.sfx,
      metadata: cart.metadata,
    })
  }, [cart.codeFiles, cart.sprites, cart.map, cart.sfx, cart.metadata])

  // Check if there are unsaved cloud changes
  const hasUnsavedCloudChanges = !!(
    cart.id &&
    lastCloudSaveHash &&
    lastCloudSaveHash !== currentCartHash
  )

  // Method to update the cloud save hash after successful save
  const updateCloudSaveHash = useCallback(() => {
    setLastCloudSaveHash(currentCartHash)
    setLastCloudSaveTime(new Date())
    saveCloudSaveHash(currentCartHash)
  }, [currentCartHash])

  const scheduleAutosave = useCallback(() => {
    setIsDirty((prev) => (prev ? prev : true))
    setSaveStatus((prev) => (prev === 'unsaved' ? prev : 'unsaved'))

    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current)
    }

    saveTimeoutRef.current = window.setTimeout(() => {
      setSaveStatus('saving')
      const success = saveCurrentCart(cartRef.current)
      if (success) {
        setSaveStatus('saved')
        setIsDirty(false)
      } else {
        setSaveStatus('unsaved')
      }
    }, AUTOSAVE_DELAY)
  }, [])

  const updateCart = useCallback(
    (newCart: Cart) => {
      setCart(newCart)
      scheduleAutosave()
    },
    [scheduleAutosave],
  )

  const updateCodeFiles = useCallback(
    (codeFiles: CodeFile[]) => {
      setCart((prev) => ({ ...prev, codeFiles }))
      scheduleAutosave()
    },
    [scheduleAutosave],
  )

  const updateCodeFile = useCallback(
    (index: number, content: string) => {
      setCart((prev) => {
        const newCodeFiles = [...prev.codeFiles]
        if (newCodeFiles[index]) {
          newCodeFiles[index] = { ...newCodeFiles[index], content }
        }
        return { ...prev, codeFiles: newCodeFiles }
      })
      scheduleAutosave()
    },
    [scheduleAutosave],
  )

  const updateSprites = useCallback(
    (sprites: Uint8Array) => {
      setCart((prev) => ({
        ...prev,
        sprites: encodeBase64(sprites),
      }))
      scheduleAutosave()
    },
    [scheduleAutosave],
  )

  const updateMap = useCallback(
    (map: Uint8Array) => {
      setCart((prev) => ({
        ...prev,
        map: encodeBase64(map),
      }))
      scheduleAutosave()
    },
    [scheduleAutosave],
  )

  const updateSfx = useCallback(
    (sfxBank: SFX[]) => {
      setCart((prev) => ({
        ...prev,
        sfx: encodeSFX(sfxBank),
      }))
      scheduleAutosave()
    },
    [scheduleAutosave],
  )

  const updateMetadata = useCallback(
    (metadata: Partial<CartMetadata>) => {
      setCart((prev) => ({
        ...prev,
        metadata: { ...prev.metadata, ...metadata },
      }))
      scheduleAutosave()
    },
    [scheduleAutosave],
  )

  const createNewCart = useCallback((metadata?: Partial<CartMetadata>) => {
    const newCart = createEmptyCart(metadata)
    setCart(newCart)
    saveCurrentCart(newCart)
    setIsDirty(false)
    setSaveStatus('saved')
    setCartLoadVersion((v) => v + 1)
    setLastCloudSaveHash(null)
    saveCloudSaveHash(null)
  }, [])

  const loadCart = useCallback((newCart: Cart, ownerId?: string) => {
    setCart(newCart)
    saveCurrentCart(newCart)
    setIsDirty(false)
    setSaveStatus('saved')
    setCartLoadVersion((v) => v + 1)

    // Persist cloud save hash for loaded carts
    if (newCart.id) {
      const hash = JSON.stringify({
        codeFiles: newCart.codeFiles,
        sprites: newCart.sprites,
        map: newCart.map,
        sfx: newCart.sfx,
        metadata: newCart.metadata,
      })
      setLastCloudSaveHash(hash)
      saveCloudSaveHash(hash)
      if (newCart.updatedAt) {
        setLastCloudSaveTime(new Date(newCart.updatedAt))
      }
    } else {
      setLastCloudSaveHash(null)
      saveCloudSaveHash(null)
    }
  }, [])

  const addCodeFile = useCallback(
    (name: string): boolean => {
      if (cart.codeFiles.length >= MAX_CODE_FILES) {
        return false
      }

      // Check for duplicate names
      if (
        cart.codeFiles.some((f) => f.name.toLowerCase() === name.toLowerCase())
      ) {
        return false
      }

      const newFile: CodeFile = {
        name,
        content: '// New file\n',
      }

      setCart((prev) => ({
        ...prev,
        codeFiles: [...prev.codeFiles, newFile],
      }))
      scheduleAutosave()
      return true
    },
    [cart.codeFiles, scheduleAutosave],
  )

  const removeCodeFile = useCallback(
    (index: number): boolean => {
      if (cart.codeFiles.length <= 1) {
        return false
      }

      setCart((prev) => ({
        ...prev,
        codeFiles: prev.codeFiles.filter((_, i) => i !== index),
      }))
      scheduleAutosave()
      return true
    },
    [cart.codeFiles.length, scheduleAutosave],
  )

  const renameCodeFile = useCallback(
    (index: number, newName: string): boolean => {
      const trimmed = newName.trim()

      // Validate name
      if (!trimmed || trimmed.length > 30) {
        return false
      }

      // Check for duplicates (excluding current file)
      if (
        cart.codeFiles.some(
          (f, i) =>
            i !== index && f.name.toLowerCase() === trimmed.toLowerCase(),
        )
      ) {
        return false
      }

      setCart((prev) => {
        const newCodeFiles = [...prev.codeFiles]
        if (newCodeFiles[index]) {
          newCodeFiles[index] = { ...newCodeFiles[index], name: trimmed }
        }
        return { ...prev, codeFiles: newCodeFiles }
      })
      scheduleAutosave()
      return true
    },
    [cart.codeFiles, scheduleAutosave],
  )

  const saveToCloud = useCallback(async () => {
    if (!cloudSaveEnabled) {
      throw new Error('Cloud storage is not configured')
    }

    setSaveStatus('saving')

    try {
      // Use existing cart ID or generate a new one
      const cartId = cart.id || crypto.randomUUID()
      let thumbnailUrl: string | undefined

      // Save cart to database first (without thumbnail) so the row exists
      // before uploading to storage (RLS policies require the cart row to exist)
      const cartToSave: Cart = {
        ...cart,
        id: cartId,
        thumbnailUrl: cart.thumbnailUrl,
      }

      const result = await CartService.saveCart(cartToSave)

      // Wait 50ms to ensure the game has rendered at least one frame
      await new Promise((resolve) => setTimeout(resolve, 50))

      // Capture and upload thumbnail now that the cart row exists
      try {
        const dataUrl = await captureCanvas()
        if (dataUrl) {
          // Delete old thumbnail if it exists
          if (cart.thumbnailUrl) {
            try {
              await ThumbnailService.deleteThumbnail(cartId)
            } catch (err) {
              console.warn('Failed to delete old thumbnail:', err)
              // Continue anyway - old thumbnail will be overwritten
            }
          }

          // Upload new thumbnail
          thumbnailUrl = await ThumbnailService.generateAndUploadFromDataUrl(
            cartId,
            dataUrl,
          )

          // Update the cart row with the thumbnail URL
          await CartService.saveCart({ ...cartToSave, thumbnailUrl })
        }
      } catch (err) {
        console.warn('Failed to capture thumbnail:', err)
        // Keep existing thumbnail URL if capture fails
        thumbnailUrl = cart.thumbnailUrl
      }

      CartLibraryCache.invalidateAll()

      // Update cart with ID, thumbnail, and updatedAt
      setCart((prev) => ({
        ...prev,
        id: result.id,
        thumbnailUrl: thumbnailUrl ?? cart.thumbnailUrl,
        updatedAt: result.updatedAt,
      }))

      // Also save locally
      saveCurrentCart({
        ...cart,
        id: result.id,
        thumbnailUrl: thumbnailUrl ?? cart.thumbnailUrl,
        updatedAt: result.updatedAt,
      })

      // Persist the cloud save hash
      const savedHash = JSON.stringify({
        codeFiles: cart.codeFiles,
        sprites: cart.sprites,
        map: cart.map,
        sfx: cart.sfx,
        metadata: cart.metadata,
      })
      setLastCloudSaveHash(savedHash)
      setLastCloudSaveTime(new Date())
      saveCloudSaveHash(savedHash)

      setIsDirty(false)
      setSaveStatus('saved')
    } catch (error) {
      setSaveStatus('unsaved')
      throw error
    }
  }, [cart, cloudSaveEnabled, captureCanvas])

  const forkCurrentCart = useCallback(async (): Promise<string> => {
    if (!cloudSaveEnabled) {
      throw new Error('Cloud storage is not configured')
    }

    if (!cart.id) {
      throw new Error('Cannot fork an unsaved cart')
    }

    setSaveStatus('saving')

    try {
      // Fork the cart
      const newCartId = await CartService.forkCart(cart.id)
      CartLibraryCache.invalidateAll()

      // Load the forked cart
      const forkedCart = await CartService.loadCart(newCartId)

      // Update local state with forked cart
      setCart(forkedCart)
      saveCurrentCart(forkedCart)

      setIsDirty(false)
      setSaveStatus('saved')

      return newCartId
    } catch (error) {
      setSaveStatus('unsaved')
      throw error
    }
  }, [cart.id, cloudSaveEnabled])

  const markClean = useCallback(() => {
    setIsDirty(false)
    setSaveStatus('saved')
  }, [])

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current)
      }
    }
  }, [])

  // Warn before unload if dirty
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty) {
        e.preventDefault()
        e.returnValue = ''
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [isDirty])

  return (
    <CartContext.Provider
      value={useMemo(
        () => ({
          cart,
          updateCart,
          updateCodeFiles,
          updateCodeFile,
          updateSprites,
          updateMap,
          updateSfx,
          updateMetadata,
          createNewCart,
          loadCart,
          addCodeFile,
          removeCodeFile,
          renameCodeFile,
          saveToCloud,
          forkCurrentCart,
          isDirty,
          markClean,
          activeTab,
          activeView,
          lastEditorTab,
          setActiveTab,
          saveStatus,
          cloudSaveEnabled,
          hasUnsavedCloudChanges,
          lastCloudSaveTime,
          currentCartHash,
          updateCloudSaveHash,
          cartLoadVersion,
        }),
        [
          cart,
          updateCart,
          updateCodeFiles,
          updateCodeFile,
          updateSprites,
          updateMap,
          updateSfx,
          updateMetadata,
          createNewCart,
          loadCart,
          addCodeFile,
          removeCodeFile,
          renameCodeFile,
          saveToCloud,
          forkCurrentCart,
          isDirty,
          markClean,
          activeTab,
          activeView,
          lastEditorTab,
          setActiveTab,
          saveStatus,
          cloudSaveEnabled,
          hasUnsavedCloudChanges,
          lastCloudSaveTime,
          currentCartHash,
          updateCloudSaveHash,
          cartLoadVersion,
        ],
      )}
    >
      {children}
    </CartContext.Provider>
  )
}

export { CartContext }

export function useCart(): CartContextType {
  const context = useContext(CartContext)
  if (!context) {
    throw new Error('useCart must be used within a CartProvider')
  }
  return context
}
